<script setup lang="ts">

import DestinationPageHero from "@/pages/DestinationPage/DestinationPageHero.vue";
import {onMounted, ref, watch, nextTick, onBeforeUnmount, computed} from "vue";
import DestinationSection from "@/components/DestinationSection/DestinationSection.vue";
import CustomButton from "@/components/Button/Button.vue";
import ArticleCard from "@/components/ArticleCard/ArticleCard.vue";
import {useDestinationStore} from "@/stores/destinationStore";
import {useBlogStore} from "@/stores/blogStore";
import {useRoute, useRouter} from "vue-router";
import {useArticleStore} from "@/stores/articleStore";
import {useWindowSize} from "@vueuse/core";


const route = useRoute();
const destinationStore = useDestinationStore();
const blogStore = useBlogStore();
const articleStore = useArticleStore();
const router = useRouter();

const slug = "travel-inspiration";

const activeLink = ref('');


const { width } = useWindowSize();
const isMobileSmall = computed(() => width.value < 768);


async function scrollToElementWithRetry(id: string, retries = 5, delay = 200) {
  for (let i = 0; i < retries; i++) {
    await new Promise(res => setTimeout(res, delay));
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    }
  }
  return false;
}

onMounted(async () => {
  const scrollTarget = (route.query.scrollTo || route.query.dest) as string;

  blogStore.getAllArticles(slug, blogStore.currentPage, blogStore.articlesPerPage);
  await destinationStore.getAllDestinations();

  destinationStore.selectedDestSlug = route.params.slug as string;
  await destinationStore.fetchSelectedDest(destinationStore.selectedDestSlug);

  await nextTick();

  if (scrollTarget) {
    await scrollToElementWithRetry(scrollTarget);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }


  watch(
      () => destinationStore.selectedDest?.destinations,
      async (destinations) => {
        if (!destinations?.length) return;

        await nextTick(); // wait for DOM to update
        setupObserver();
      },
      { immediate: true }
  );

});


function scrollToDestination(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    router.replace({ path: route.path, query: { dest: id } }); // update query param
  }
}
// function scrollToDestination(id: string) {
//   scrollToElementWithRetry(id);
//   router.replace({ path: route.path, query: { dest: id } });
// }



let observer: IntersectionObserver | null = null;

function setupObserver() {
  if (!destinationStore.selectedDest?.destinations?.length) return;

  observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            if (id && id !== activeLink.value) {
              activeLink.value = id;
              router.replace({ path: route.path, query: { dest: id } });
            }
          }
        });
      },
      {
        threshold: isMobileSmall ? 0.5 : 0.8,
        rootMargin: isMobileSmall ? '0px 0px -30% 0px' : '25% 0px -20% 0px',

      }
  );

  destinationStore.selectedDest.destinations.forEach((destinationItem) => {
    const el = document.getElementById(destinationItem.name);
    if (el) observer!.observe(el);
  });
}

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});


</script>

<template>
<DestinationPageHero :destination="destinationStore.selectedDest"/>
  <div class="container py-20 responsive-title-4 text-primary-darkBlue font-thin " v-html="destinationStore.selectedDest?.description"></div>

  <div class="flex container">


    <!-- DESKTOP: LEFT SIDE NAVIGATION -->
    <nav class="w-48 hidden lg:block px-4 sticky top-[35%] h-[65%] overflow-y-auto mt-20">
      <ul class="space-y-8 responsive-body-xlarge font-semibold text-primary-blueGrey">
        <li
            v-for="(destinationItem, index) in destinationStore.selectedDest?.destinations"
            :key="destinationItem.name"
            :class="[
              activeLink === destinationItem.name ? 'border-l-4 border-primary-violet text-primary-darkBlue' : '',
              'hover:text-primary-darkBlue',
              'pl-4',
              'cursor-pointer'
            ]"
            @click="() => scrollToDestination(destinationItem.name)"
        >
          {{ destinationItem.name }}
        </li>
      </ul>

    </nav>
    <div class="flex-1 mx-auto px-4">

      <!-- MOBILE: TOP NAVIGATION -->
      <nav class="hidden sm:block lg:hidden sticky top-[70px] w-full mb-20 z-50 bg-background-beige border px-4 py-2">


        <ul class="flex flex-wrap justify-center gap-x-4 gap-y-2 text-primary-blueGrey font-semibold responsive-body-medium">
          <li
              v-for="(destinationItem, index) in destinationStore.selectedDest?.destinations"
              :key="destinationItem.name"
              :class="[
                activeLink === destinationItem.name
                  ? 'border-b-2 border-primary-violet text-primary-darkBlue'
                  : '',
                'hover:text-primary-darkBlue',
                'lg:pl-4',
                'cursor-pointer whitespace-nowrap'
              ]"
              @click="scrollToDestination(destinationItem.name)"
          >
            {{ destinationItem.name }}
          </li>

        </ul>
      </nav>
<!--      <div class="pb-20 responsive-body-large text-bw-midGrey" v-html="destinationStore.selectedDest.description"></div>-->

      <div class="space-y-25">
        <div
          v-for="(destinationItem, index) in destinationStore.selectedDest?.destinations"
          :key="destinationItem.name"
          :id="destinationItem.name"
          class="sm:scroll-mt-32"
        >
          <DestinationSection :destination="destinationItem" :index="index" />
        </div>
      </div>
    </div>
  </div>
  <div class="mt-32 bg-white py-24">
    <div class="container">

      <div class="responsive-title-1 text-center mb-4 text-nowrap text-primary-darkBlue font-thin">Related articles</div>
      <div class="flex flex-col gap-17 place-content-center items-center pt-24 px-1 overflow-x-hidden">
        <div class="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="(page, index) in blogStore.allArticles.slice(0, 3)" :key="index">
            <ArticleCard
                :page="page"
                @on-visit-article="articleStore.visitArticle(page.slug)"
            />
          </div>
        </div>
        <CustomButton
            type="gradient"
            :outlined="true"
            size="medium"
            :shadow="false"
            class="py-2 px-4"
            @click="router.push('/magazine')"
        >
          READ MORE ARTICLES
        </CustomButton>
      </div>
    </div>
  </div>

</template>

<style scoped>

</style>
